import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useSettings, buildWaLink, normalizeWhatsapp } from '../contexts/SettingsContext';
import { trackFormSubmission } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Format a normalized WhatsApp number (`5547999201212`) as `+55 47 99920-1212`.
const formatBRPhone = (num) => {
  const n = normalizeWhatsapp(num);
  if (!n) return '';
  // Expected: 55 + DD + 8-9 digits.
  const cc = n.slice(0, 2);
  const dd = n.slice(2, 4);
  const rest = n.slice(4);
  if (rest.length === 9) return `+${cc} ${dd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
  if (rest.length === 8) return `+${cc} ${dd} ${rest.slice(0, 4)}-${rest.slice(4)}`;
  return `+${cc} ${dd} ${rest}`;
};

const Contact = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 },
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/public/contact`, form);
      trackFormSubmission('contato');
      toast.success(t('contact.success'));
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const channels = [
    { icon: 'solar:letter-linear', key: 'email', value: 'contato@ieneagrama.com.br', href: 'mailto:contato@ieneagrama.com.br' },
    { icon: 'solar:phone-linear', key: 'whatsapp', value: formatBRPhone(settings.whatsapp_number), href: buildWaLink(settings.whatsapp_number) },
    { icon: 'solar:map-point-linear', key: 'address', value: 'Porto Alegre — RS, Brasil', href: '#' },
    { icon: 'solar:clock-circle-linear', key: 'hours', value: 'Seg–Sex · 09h–18h', href: '#' },
  ];

  return (
    <div data-testid="contact-page">
      <Header />
      <main className="pt-20">
        <section className="relative py-24 border-b border-neutral-900 overflow-hidden" style={{ background: 'rgba(10, 10, 10, 0.72)' }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block animate-on-scroll">
                {t('pages.contact.hero_label')}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 animate-on-scroll delay-100 text-[#EAEAEA]">
                {t('pages.contact.title_part1')} <span className="text-accent-orange">{t('pages.contact.title_highlight')}</span>
              </h1>
              <p className="text-lg text-neutral-400 leading-relaxed animate-on-scroll delay-200">
                {t('pages.contact.hero_subtitle')}
              </p>
            </div>
          </div>
        </section>

        <section className="relative py-16" style={{ background: 'rgba(17, 17, 17, 0.78)' }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {channels.map((c, i) => (
                <a
                  key={c.key}
                  href={c.href}
                  className="glow-card bg-[#1A1A1A] border border-[#333333] p-6 rounded-xl text-center hover-lift block animate-on-scroll"
                  style={{ transitionDelay: `${i * 0.08}s` }}
                  data-testid={`contact-channel-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center mx-auto mb-3 text-accent-orange">
                    <iconify-icon icon={c.icon} className="text-2xl"></iconify-icon>
                  </div>
                  <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">{t(`contact.channels.${c.key}`)}</div>
                  <div className="text-sm font-medium text-[#EAEAEA]">{c.value}</div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-24" style={{ background: 'rgba(10, 10, 10, 0.72)' }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">{t('pages.contact.form_title')}</h2>
                <p className="text-lg text-white/80">{t('pages.contact.form_subtitle')}</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t('contact.field_name')}
                    data-testid="contact-name-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t('contact.field_email')}
                    data-testid="contact-email-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder={t('contact.field_phone')}
                    data-testid="contact-phone-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                  <select
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    data-testid="contact-subject-select"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-white/40"
                  >
                    <option value="" className="bg-neutral-800">{t('contact.field_subject')}</option>
                    <option className="bg-neutral-800">{t('contact.subject_options.individual')}</option>
                    <option className="bg-neutral-800">{t('contact.subject_options.corporate')}</option>
                    <option className="bg-neutral-800">{t('contact.subject_options.franchisee')}</option>
                    <option className="bg-neutral-800">{t('contact.subject_options.job')}</option>
                    <option className="bg-neutral-800">{t('contact.subject_options.press')}</option>
                    <option className="bg-neutral-800">{t('contact.subject_options.other')}</option>
                  </select>
                </div>
                <textarea
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t('contact.field_message')}
                  data-testid="contact-message-input"
                  rows="5"
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40 resize-none"
                ></textarea>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="w-full bg-accent-orange text-white hover:bg-[#E55D00] h-12 text-sm font-bold uppercase tracking-wider"
                >
                  {submitting ? t('common.sending') : t('contact.submit')}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
